import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Menu, X } from '../../shared/icons';
import { useAuth } from '../auth/AuthProvider';
import { navigate } from '../../app/router';
import { cn } from '../../shared/utils/cn';

export const Navbar = () => {
  const { user, logout, openAuthModal } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Plan 123 A — transparent at the top of the page so the hero bleeds to
  // the viewport edge; the frosted background fades in only once the user
  // has scrolled half a viewport (owner wants the hero to own the whole
  // first screen — an early fade-in reads as a white bar re-appearing).
  // The background is its own opacity-transitioned layer (rather than
  // toggling bg classes on <nav>) so the backdrop blur fades with it.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.5);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // With the mobile menu open, the bar must be opaque regardless of scroll
  // position — its dropdown panel is, and a transparent bar above it reads
  // as a rendering glitch.
  const showBackground = scrolled || isOpen;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div
        aria-hidden="true"
        data-testid="navbar-background"
        className={cn(
          'absolute inset-0 bg-stone-50/80 backdrop-blur-md border-b border-stone-200 transition-opacity duration-300',
          showBackground ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div className="relative max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="#" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src="/icon-192.png" alt="" width={40} height={40} className="w-10 h-10" />
          {/* Logo wordmark is Montserrat (font-sans), per owner 2026-07-11. */}
          <span className="font-sans font-semibold text-xl tracking-tight">HDMAVEN.XYZ</span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          <a
            href="#services"
            className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Services
          </a>
          <a
            href="#about"
            className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            About
          </a>
          <a
            href="/blog"
            className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Blog
          </a>
          <a
            href="#contact"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Contact
          </a>
          {user ? (
            <div className="flex items-center gap-4">
              {user.role === 'admin' ? (
                <button
                  onClick={() => navigate('admin')}
                  className="text-sm font-bold text-stone-900 underline underline-offset-4"
                >
                  Admin Portal
                </button>
              ) : (
                <button
                  onClick={() => navigate('dashboard')}
                  className="text-sm font-medium text-stone-900 underline underline-offset-4"
                >
                  Dashboard
                </button>
              )}
              <button
                onClick={logout}
                className="px-4 py-2 text-xs font-bold tracking-widest text-stone-600 hover:text-stone-900 transition-colors uppercase"
              >
                Log Out
              </button>
            </div>
          ) : (
            <button
              onClick={() => openAuthModal()}
              className="bg-stone-900 text-stone-50 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-stone-800 transition-colors"
            >
              Client Access
            </button>
          )}
        </div>

        <button
          className="md:hidden"
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            // `relative` is load-bearing: the frosted background layer above
            // is absolutely positioned and paints OVER static siblings — an
            // unpositioned dropdown appears for a beat, then vanishes under
            // the layer as its opacity transition completes.
            className="relative md:hidden bg-stone-50 border-b border-stone-200 px-6 py-8 flex flex-col gap-6"
          >
            <a href="#services" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              Services
            </a>
            <a href="#about" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              About
            </a>
            <a href="/blog" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              Blog
            </a>
            <a
              href="#contact"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="text-lg font-medium"
            >
              Contact
            </a>
            {user ? (
              <>
                <button
                  onClick={() => {
                    navigate('dashboard');
                    setIsOpen(false);
                  }}
                  className="text-lg font-medium text-left"
                >
                  Dashboard
                </button>
                <button onClick={logout} className="text-lg font-medium text-left text-red-600">
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  openAuthModal();
                  setIsOpen(false);
                }}
                className="bg-stone-900 text-stone-50 px-6 py-3 rounded-full text-lg font-medium"
              >
                Client Access
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
